import { useParams } from "@solidjs/router";
import { createSignal, onMount, Show } from "solid-js";
import { Grant } from "./MyGrants";
import { useSFetch } from "../hooks/sfetch";
import { For } from "solid-js";
import { useRoleParser } from "../hooks/permissions";
import Text from "../components/Text";
import { showConfirm } from '../components/Dialog';
import { User } from "../components/UserContextProvider";
import { useToast } from "../components/MessageBox";
import { useTranslation } from "../hooks/useTranslation";
import { Command } from "./MyCommands";
import { formatDateTime } from "../services/dateFormat";

export interface GrantedPrefab {
    grantId: string;
    prefabId: string;
    id: string;
    createdAt: string;
}

export function GrantPage() {
    const toast = useToast();
    const { t } = useTranslation();
    const roles = ["Read", "Admin"];
    const { parseRoles } = useRoleParser();
    const sfetch = useSFetch();
    const params = useParams();
    const [grant, setGrant] = createSignal<Grant>();
    const [allUsers, setAllUsers] = createSignal<User[]>([]);
    const [permissions, setPermissions] = createSignal<boolean[]>([false, false]);
    const [prefabs, setPrefabs] = createSignal<Command[]>([]);
    const [grantedPrefabs, setGrantedPrefabs] = createSignal<GrantedPrefab[]>([]);
    const [selectedPrefabId, setSelectedPrefabId] = createSignal<string | undefined>(undefined);
    const [loading, setLoading] = createSignal(true);
    const [saveLoading, setSaveLoading] = createSignal(false);
    const [grantLoading, setGrantLoading] = createSignal(false);

    function setPermissionAtIndex(index: number, e: Event) {
        const newPermissions = [...permissions()];
        newPermissions[index] = (e.target as HTMLInputElement).checked;
        setPermissions(newPermissions);
    }

    function permissionInteger(roles: boolean[]): number {
        return roles.reduce((acc, role, i) => acc | (role ? 1 << i : 0), 0);
    }

    onMount(async () => {
        try {
            const response = await sfetch(`/api/GrantedUsers/GetGrantedAccounts`);
            if (!response.ok) { setLoading(false); return; }
            const foundGrant = response.value?.find((g: Grant) => g.id === params.id);
            setGrant(foundGrant);
            setPermissions(parseRoles(foundGrant?.level ?? 0, roles));

            const usersResponse = await sfetch('/api/User/Users');
            if (usersResponse.ok) setAllUsers(usersResponse.value ?? []);

            if (!foundGrant) {
                toast?.error(t('GRANT_NOT_FOUND'));
                setLoading(false);
                return;
            }

            const prefabsRes = await sfetch('/api/GrantedUsers/GetCommandPrefabs');
            if (prefabsRes.ok) setPrefabs(prefabsRes.value ?? []);

            const grantedRes = await sfetch(`/api/GrantedUsers/GetCommandPrefabsInGrant/${foundGrant.id}`);
            if (grantedRes.ok) setGrantedPrefabs(grantedRes.value ?? []);
        } catch (error) {
            toast?.error(t('LOADING') + '...');
        }
        setLoading(false);
    });

    async function save(e: Event) {
        e.preventDefault();
        setSaveLoading(true);
        const res = await sfetch(`/api/GrantedUsers/ModifyAccessLevel/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                accessLevel: permissionInteger(permissions()),
                grantId: grant()?.id
            })
        });
        setSaveLoading(false);
        if (res.ok) toast?.info(t('GRANT_PERMISSIONS_SAVED'));
        else toast?.error(t('GRANT_SAVE_FAILED'));
    }

    async function grantPrefab(e: Event) {
        e.preventDefault();
        if (!selectedPrefabId()) {
            toast?.error(t('GRANT_SELECT_COMMAND'));
            return;
        }

        setGrantLoading(true);
        const res = await sfetch(`/api/GrantedUsers/GrantCommandPrefab`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prefabId: selectedPrefabId(),
                grantId: grant()?.id
            })
        });
        setGrantLoading(false);
        setSelectedPrefabId(undefined);
        if (res.ok) {
            toast?.info(t('GRANT_COMMAND_GRANTED'));
            const updated = await sfetch(`/api/GrantedUsers/GetCommandPrefabsInGrant/${grant()?.id}`);
            if (updated.ok) setGrantedPrefabs(updated.value ?? []);
        } else {
            toast?.error(t('GRANT_COMMAND_FAILED'));
        }
    }

    async function revokeCommandPrefab(e: Event, id: string) {
        e.preventDefault();
        if (!await showConfirm(t('GRANT_REVOKE_COMMAND_CONFIRM'), true)) return;

        const res = await sfetch(`/api/GrantedUsers/RevokeCommandPrefab`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prefabId: id,
                grantId: grant()?.id
            })
        });
        setSelectedPrefabId(undefined);
        if (res.ok) {
            toast?.info(t('GRANT_COMMAND_REVOKED'));
            const updated = await sfetch(`/api/GrantedUsers/GetCommandPrefabsInGrant/${grant()?.id}`);
            if (updated.ok) setGrantedPrefabs(updated.value ?? []);
        } else {
            toast?.error(t('GRANT_REVOKE_FAILED'));
        }
    }

    const userName = () => allUsers().find((user) => user.id === grant()?.userId)?.username || "-";

    const getPrefabForGranted = (prefabId: string) => prefabs().find(x => x.id === prefabId);

    return (
        <div class="dashboard-page">
            <h1><Text val="GRANTDETAILS" /></h1>
            <Show when={loading()}>
                <p class="gray"><Text val="LOADING" /></p>
            </Show>
            <Show when={!loading() && grant()}>
                <div class="h wrap">
                    <div class="fcard v">
                        <h2><Text val="DETAILS" /></h2>
                        <div class="field-group">
                            <span class="field-label"><Text val="USERNAME" /></span>
                            <span class="field-value">{userName()}</span>
                        </div>
                        <div class="field-group">
                            <span class="field-label"><Text val="CREATEDAT" /></span>
                            <span class="field-value">{formatDateTime(grant()!.createdAt)}</span>
                        </div>
                    </div>
                    <div class="fcard v">
                        <h2><Text val="PERMISSIONS" /></h2>
                        <div class="h wrap">
                            <For each={roles}>
                                {(role, i) => (
                                    <div class="permission-item">
                                        <label>
                                            <input
                                                type="checkbox"
                                                onChange={(e) => setPermissionAtIndex(i(), e)}
                                                checked={permissions()[i()]}
                                            />
                                            {role}
                                        </label>
                                    </div>
                                )}
                            </For>
                        </div>
                        <button onClick={save} disabled={saveLoading()}>{saveLoading() ? '...' : <Text val="SAVECHANGES" />}</button>
                    </div>
                </div>
                <div class="fcard v">
                    <h2><Text val="GRANTEDCOMMANDS" /></h2>
                    <div class="h">
                        <select class="language-select" value={selectedPrefabId()} onChange={
                            (e) => setSelectedPrefabId((e.target as HTMLSelectElement).value)
                        }>
                            <option value=""><Text val="SELECTCOMMAND" /></option>
                            <For each={prefabs().filter(x => !grantedPrefabs().some(y => y.prefabId === x.id))}>
                                {(command) => (
                                    <option value={command.id}>{command.name || command.template}</option>
                                )}
                            </For>
                        </select>
                        <button onClick={grantPrefab} disabled={!selectedPrefabId() || grantLoading()}>
                            <Text val="GRANTCOMMAND" />
                        </button>
                    </div>
                    <Show when={grantedPrefabs().length === 0}>
                        <p class="gray"><Text val="GRANT_NO_COMMANDS" /></p>
                    </Show>
                    <Show when={grantedPrefabs().length > 0}>
                        <table class="sessiontable">
                            <thead>
                                <tr>
                                    <th><Text val="NAME" /></th>
                                    <th><Text val="TEMPLATE" /></th>
                                    <th><Text val="ONLYPREFIX" /></th>
                                    <th><Text val="CREATEDAT" /></th>
                                    <th><i class="fa-solid fa-trash"></i></th>
                                </tr>
                            </thead>
                            <tbody>
                                <For each={grantedPrefabs()}>
                                    {(command) => {
                                        const prefab = () => getPrefabForGranted(command.prefabId);
                                        return (
                                            <tr>
                                                <td>{prefab()?.name ?? "-"}</td>
                                                <td>{prefab()?.template ?? "-"}</td>
                                                <td>{prefab()?.onlyPrefix ? "Yes" : "No"}</td>
                                                <td>{formatDateTime(command.createdAt)}</td>
                                                <td><button onClick={(e) => revokeCommandPrefab(e, command.prefabId)} class='danger'><i class="fa-solid fa-trash"></i></button></td>
                                            </tr>
                                        );
                                    }}
                                </For>
                            </tbody>
                        </table>
                    </Show>
                </div>
            </Show>
        </div>
    );
}
